<script>
import { formatAuthorValue } from "@/utils/format";
import RubricLibraryView from "@/components/rubrics/rubric_library_view.vue";
import RubricForm from "@/components/rubrics/rubric_form.vue";
import SettingsSidebar from "@/components/rubrics/newRubric/settings_sidebar.vue";
import RubricLibrarySidebar from "@/components/rubrics/rubric_library.vue";
import { getSingleRubric } from "@/api";
import RubricFullscreenModal from "@/components/rubrics/components/rubric_fullscreen_modal.vue";
import RubricFormActions from "@/components/rubrics/newRubric/components/rubric_form_actions.vue";
import RubricSettingsTab from "@/components/rubrics/components/rubric_settings_tab.vue";
import RubricPreviewTab from "@/components/rubrics/components/rubric_preview_tab.vue";
import rubricFormStateMixin from "@/mixins/rubricFormStateMixin";

export default {
  name: "RubricFromTemplate",
  components: {
    RubricLibrarySidebar,
    SettingsSidebar,
    RubricForm,
    RubricLibraryView,
    RubricFullscreenModal,
    RubricFormActions,
    RubricSettingsTab,
    RubricPreviewTab,
  },
  mixins: [rubricFormStateMixin],
  inject: ["clearRubricSettings"],
  emits: ["createRubric", "close"],
  data() {
    return {
      step: 0,
      selectedRubric: null,
    };
  },
  beforeMount() {
    this.clearRubricSettings();
  },
  methods: {
    formatAuthorValue,
    async onSelectRubric(rubric) {
      const isChangingTemplate = !!this.selectedRubric;
      const { data } = await getSingleRubric(rubric.id);
      this.selectedRubric = data;
      this.updateSettingsFromRubric(data);
      this.step = 1;
      if (!isChangingTemplate) {
        this.emitRubricBuilderOpened();
      }
    },
  },
};
</script>

<template>
  <div class="rubric-from-template-wrapper">
    <!-- Fullscreen Rubric Form -->
    <RubricLibrarySidebar
      :value="selectedRubric"
      :auto-commit="false"
      hide-create-button
      @input="onSelectRubric"
    />

    <RubricFullscreenModal
      v-show="step === 1"
      ref="fullscreenModal"
      mode="create"
      :show-tabs="true"
      @cancel="$emit('close')"
      @save="$refs.rubricForm.handleSubmit()"
      @back="step = 0"
    >
      <!-- Edit Tab Content -->
      <template #default>
        <SettingsSidebar intensity grade language />

        <div>
          <rubric-form
            v-if="selectedRubric"
            :key="`template-${selectedRubric.id}`"
            ref="rubricForm"
            :rubric="selectedRubric"
            :editing="false"
            :hide-actions="true"
            :initial-data="rubricFormData"
            @rubricSubmitted="$emit('createRubric', $event)"
            @formDataUpdate="handleFormDataUpdate"
            @switchToFirstTab="$refs.fullscreenModal.activeTab = 0"
            @close="$emit('close')"
          >
            <!-- Insert Rubric Template block before criteria -->
            <template #before-criteria>
              <div class="rubric-template-section">
                <label class="template-label">{{
                  $t("rubrics.rubricFallback")
                }}</label>
                <div class="template-content">
                  <div class="template-name">
                    {{ selectedRubric?.name }}
                  </div>
                  <eg-button
                    v-b-toggle.rubric-library-sidebar
                    type="secondary"
                    size="small"
                  >
                    {{ $t("rubrics.changeTemplate") }}
                  </eg-button>
                </div>
              </div>
            </template>
          </rubric-form>
        </div>
      </template>

      <!-- Preview Tab Content -->
      <template #preview>
        <div class="preview-wrapper">
          <RubricPreviewTab
            :key="`preview-${rubricFormData.name}-${
              rubricFormData.criterias?.length || 0
            }`"
            :rubric="rubricFormData"
          />
        </div>
      </template>

      <!-- Settings Tab Content -->
      <template #settings>
        <RubricSettingsTab intensity grade language />
      </template>
    </RubricFullscreenModal>

    <!-- Normal Modal Content -->
    <div v-show="step === 0" class="rubrics-list-container">
      <div class="rubric-table-container">
        <RubricLibraryView
          :value="selectedRubric"
          :show-add-new="false"
          @selectRubric="onSelectRubric"
        />
      </div>
      <RubricFormActions :on-cancel="() => $emit('close')" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.rubric-from-template-wrapper {
  position: relative;
  width: 100%;
  &::v-deep {
    .rubrics-list-container {
      position: relative;
      width: 100%;
      margin-top: 1rem;
      h3 {
        padding: 24px 24px 0 24px;
      }
      .eg-tabs__container .nav-tabs {
        padding: 0;
        margin: 0 1.5rem;
      }
      .tab-content,
      .rubric-filter {
        background-color: var(--color-surface-neutral-default);
      }
      .tab-content {
        padding: 0 1.5rem;
      }
    }
    .b-sidebar-outer {
      z-index: 1057;
    }
  }
}

.rubric-table-container {
  height: 100%;
  overflow: auto;

  &::v-deep {
    .rubric-filter {
      position: sticky;
      top: 0;
      z-index: 3;
    }
    .tab-content {
      height: calc(85vh - 278px);
    }
  }
}

.preview-wrapper {
  margin: 0 auto;
}

.rubric-template-section {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.template-label {
  color: var(--color-text-neutral-on-white, #1a1a1a);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0;
}

.template-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.template-name {
  color: var(--color-text-neutral-subtle, #6b7280);
  font-size: 16px;
  line-height: 1.5;
}
</style>
